import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Handshake, HandCoins, Check } from "lucide-react";

const TYPES = [
  {
    id: "AVAILABLE_FOR_RENTAL",
    title: "I'm Available for Rental",
    desc: "I'm offering my availability and looking to be rented/engaged by someone.",
    Icon: Handshake,
    color: "emerald",
  },
  {
    id: "LOOKING_TO_RENT",
    title: "I'm Looking to Rent Someone",
    desc: "I'm looking for a person who is available to be rented/engaged.",
    Icon: HandCoins,
    color: "indigo",
  },
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", city: "", area: "", pincode: "", bio: "", phone: "", profile_type: "" });
  const [saving, setSaving] = useState(false);
  const [typeError, setTypeError] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        city: user.city || "",
        area: user.area || "",
        pincode: user.pincode || "",
        bio: user.bio || "",
        phone: user.phone || "",
        profile_type: user.profile_type || "",
      });
    }
  }, [user]);

  const selectType = (id) => { setForm({ ...form, profile_type: id }); setTypeError(""); };

  const save = async () => {
    if (!form.profile_type) { setTypeError("Please select what you are looking for."); toast.error("Please select what you are looking for."); return; }
    if (!form.name || !form.city || !form.area || !form.pincode) { toast.error("Name, city, area and pincode are required"); return; }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("Pincode must be 6 digits"); return; }
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success("Profile updated");
      if (!user?.profile_completed) navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Your Profile</h1>
        <p className="text-slate-600 mt-2">Tell us what you're looking for and where you are so the right neighbours can find you.</p>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 space-y-6" data-testid="profile-form">
          {/* What are you looking for */}
          <section>
            <div className="flex items-baseline justify-between">
              <div>
                <h2 className="font-heading font-bold text-lg text-slate-900">What are you looking for? <span className="text-rose-500">*</span></h2>
                <p className="text-xs text-slate-500 mt-0.5">Pick one. You can change this later from profile settings.</p>
              </div>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="profile-type-picker">
              {TYPES.map((t) => {
                const selected = form.profile_type === t.id;
                const ringCls = t.color === "emerald" ? "ring-emerald-500 border-emerald-500 bg-emerald-50/50" : "ring-indigo-500 border-indigo-500 bg-indigo-50/50";
                const iconCls = t.color === "emerald" ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700";
                return (
                  <button
                    type="button"
                    key={t.id}
                    data-testid={`profile-type-${t.id}`}
                    onClick={() => selectType(t.id)}
                    className={`relative text-left rounded-xl border-2 p-4 transition hover:shadow-md ${selected ? `ring-2 ${ringCls}` : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  >
                    {selected && (
                      <span className="absolute top-3 right-3 inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-600 text-white">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconCls}`}>
                      <t.Icon className="w-5 h-5" />
                    </div>
                    <div className="mt-3 font-heading font-bold text-slate-900">{t.title}</div>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">{t.desc}</p>
                  </button>
                );
              })}
            </div>
            {typeError && (<p className="mt-2 text-xs text-rose-600 font-semibold" data-testid="profile-type-error">{typeError}</p>)}
          </section>

          <section className="border-t border-slate-100 pt-6">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-teal-600 text-white flex items-center justify-center text-xl font-bold">{user?.name?.[0] || "U"}</div>
              )}
              <div>
                <div className="font-semibold text-slate-900">{user?.email}</div>
                <div className="text-xs text-slate-500">Google verified</div>
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name <span className="text-rose-500">*</span></Label>
                <Input data-testid="profile-name-input" id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input data-testid="profile-phone-input" id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="city">City <span className="text-rose-500">*</span></Label>
                <Input data-testid="profile-city-input" id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="area">Area / Locality <span className="text-rose-500">*</span></Label>
                <Input data-testid="profile-area-input" id="area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="pincode">Pincode (6 digits) <span className="text-rose-500">*</span></Label>
                <Input data-testid="profile-pincode-input" id="pincode" value={form.pincode} maxLength={6} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea data-testid="profile-bio-input" id="bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1" placeholder="A couple of lines about yourself" />
              </div>
            </div>
          </section>

          <div className="flex items-center gap-3">
            <Button data-testid="profile-save-btn" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6">
              {saving ? "Saving..." : "Save profile"}
            </Button>
            {user?.subscription_active && (
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Subscription: {user.subscription_tier}
              </span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
